import { useEffect, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { t } from "@/i18n-js/instance";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import type { Post, Space } from "@circle-react/types";
import type { Comment as CommentType } from "@circle-react/types/Comment";
import { LoaderRow } from "@circle-react-shared/LoaderRow";
import { Comment } from "../Comment";
import { CommentForm } from "../CommentForm";
import { useCommentsList } from "./useCommentsList";

export interface CommentsListProps {
  autofocusReplyForm?: boolean;
  commentsSectionRef?: React.RefObject<HTMLElement>;
  formRef: React.RefObject<unknown>;
  onCommentCreate?: (comment: CommentType) => void;
  onCommentDelete?: (comment: CommentType) => void;
  post: Post;
  preloaded?: boolean;
  preloadedComments?: CommentType[];
  preloadedCommentsTotalCount?: number;
  scrollableTarget?: React.RefObject<HTMLElement>;
  showReplyForm?: boolean;
  space: Space;
}

export const CommentsList = ({
  space,
  post,
  preloaded = false,
  preloadedComments = [],
  preloadedCommentsTotalCount = 0,
  showReplyForm = false,
  autofocusReplyForm = false,
  onCommentCreate,
  onCommentDelete,
  formRef,
  scrollableTarget,
  commentsSectionRef,
}: CommentsListProps) => {
  const {
    comments,
    hasNextPage,
    fetchNextPage,
    updateCommentInList,
    handleCommentCreate,
    handleCommentDelete,
    formKey,
    persistCacheKey,
    isFirstLoading,
    isAnyLoading,
    isUsedInMinimalTipTapViewModal,
    shouldShowForm,
  } = useCommentsList({
    parentCommentId: null,
    postId: post.id,
    preloaded,
    preloadedComments,
    preloadedCommentsTotalCount,
    postCommentsClosed: post.is_comments_closed,
    postLikingDisabled: post.is_liking_disabled,
    space,
    showReplyForm,
    onCommentCreate,
    onCommentDelete,
    commentsSectionRef,
  });

  const [scrollableTargetElement, setScrollableTargetElement] =
    useState<HTMLElement | null>(null);

  useEffect(() => {
    const target = scrollableTarget?.current;
    if (target) {
      setScrollableTargetElement(target);
    }
  }, [scrollableTarget?.current]);

  return (
    <div className={classNames({ "my-3": !shouldShowForm })}>
      <InfiniteScroll
        style={{ overflow: "initial" }}
        scrollThreshold={0.8}
        next={fetchNextPage}
        hasMore={hasNextPage ?? false}
        loader={<LoaderRow />}
        dataLength={comments?.length || 0}
        scrollableTarget={scrollableTargetElement ?? "panel"}
        /*
         * Hack to workaround the following bug in react-infinite-scroll-component:
         * https://github.com/ankeetmaini/react-infinite-scroll-component/issues/89
         *
         * The `InfiniteScroll` component adds scroll listeners only when the component
         * is mounted. That means changing the `scrollableTarget` prop has no effect, and
         * since we're using refs, the initial value is `null`, causing the scrolling
         * listener to be added to `#panel`. The solution in this case is to set
         * another prop so we can re-mount the component when the ref changes.
         * Given we know the value can only change from `null` to the ref'ed element,
         * we can use a string that differentiates the two cases.
         */
        key={scrollableTargetElement ? "elementTarget" : "idTarget"}
      >
        {comments.map((comment: CommentType) => (
          <Comment
            key={comment.id}
            space={space}
            post={post}
            comment={comment}
            autofocusReplyForm
            onUpdate={updateCommentInList}
            onDelete={handleCommentDelete}
            onReplyCreate={onCommentCreate}
            onReplyDelete={onCommentDelete}
          />
        ))}
      </InfiniteScroll>
      {hasNextPage && !isAnyLoading && (
        <div className="load-more py-5 text-center">
          <button
            type="button"
            onClick={event => {
              event.preventDefault();
              void fetchNextPage();
            }}
            className="focus-visible:outline-secondary load-more__comments border-hover bg-primary text-dark hover:text-darkest focus-visible:text-darkest inline-block rounded-md border px-6 py-2 text-sm font-semibold leading-normal transition-colors duration-150 focus-visible:rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
          >
            {t("comments.list.show_more_comments")}
          </button>
        </div>
      )}
      {isFirstLoading && (
        <LoaderRow
          className={classNames({ "mb-16": isUsedInMinimalTipTapViewModal })}
        />
      )}
      {isAnyLoading && isUsedInMinimalTipTapViewModal && (
        <div className="bg-primary absolute bottom-0 left-0 w-full !px-5 !py-4 md:!px-8">
          <div className="flex gap-4">
            <div className="bg-tertiary size-10 animate-pulse rounded-full" />
            <div className="border-primary h-10 w-full rounded-lg border p-px" />
          </div>
        </div>
      )}
      {shouldShowForm && (
        <CommentForm
          key={formKey}
          spaceId={space.id}
          postId={post.id}
          autoFocus={autofocusReplyForm}
          className={classNames(
            isUsedInMinimalTipTapViewModal && "!max-h-[360px]",
          )}
          onCreate={handleCommentCreate}
          bodyInputRef={formRef}
          persistCacheKey={persistCacheKey}
        />
      )}
    </div>
  );
};
